import { useState } from 'react';

export default function Contact() {
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMessage('');

    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        setStatus('success');
        e.target.reset();
      } else {
        const errData = await response.json().catch(() => ({}));
        setErrorMessage(errData.message || 'Der opstod en fejl ved afsendelse. Prøv igen.');
        setStatus('error');
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setErrorMessage('Netværksfejl. Kontroller din internetforbindelse og prøv igen.');
      setStatus('error');
    }
  };

  return (
    <div className="space-y-20 pb-24 pt-24">
      {/* Contact Content Section */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-12 lg:items-start">
          {/* Left: Contact Info */}
          <div className="lg:col-span-5 space-y-8">
            <div className="space-y-4">
              <h2 className="text-2xl font-bold text-slate-900">Vores kontaktinformationer</h2>
              <p className="text-slate-500 leading-relaxed">
                Du er altid velkommen til at ringe eller skrive til os. Vi bestræber os på at besvare alle skriftlige henvendelser inden for 1-3 hverdage.
              </p>
            </div>

            <div className="divide-y divide-slate-200">
              {/* Phone */}
              <div className="py-6 flex items-start gap-4 first:pt-0 last:pb-0">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center text-slate-900 text-xl">
                  <i className="fa-solid fa-phone" />
                </div>
                <div>
                  <a href="tel:+4561167778" className="text-lg font-semibold text-slate-700 hover:text-brand-accent transition-colors whitespace-nowrap block">
                    +45 61 16 77 78
                  </a>
                </div>
              </div>

              {/* Email */}
              <div className="py-6 flex items-start gap-4 first:pt-0 last:pb-0">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center text-slate-900 text-xl">
                  <i className="fa-solid fa-envelope" />
                </div>
                <div>
                  <a href="mailto:info@jc-svejseteknik.dk" className="text-lg font-semibold text-slate-700 hover:text-brand-accent transition-colors whitespace-nowrap block">
                    info@jc-svejseteknik.dk
                  </a>
                </div>
              </div>

              {/* Address */}
              <div className="py-6 flex items-start gap-4 first:pt-0 last:pb-0">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center text-slate-900 text-xl">
                  <i className="fa-solid fa-location-dot" />
                </div>
                <div>
                  <p className="text-lg font-semibold text-slate-700">Brandlundvej 11, 7330 Brande</p>
                  <a
                    href="https://www.google.com/maps/place/Brandlundvej%2011,%207330%20Brande"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-accent hover:text-brand-accent/80 transition-colors mt-2"
                  >
                    Vis på Google Maps
                    <i className="fa-solid fa-arrow-up-right-from-square text-[10px]" />
                  </a>
                </div>
              </div>

              {/* CVR */}
              <div className="py-6 flex items-start gap-4 first:pt-0 last:pb-0">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center text-slate-900 text-xl">
                  <i className="fa-solid fa-id-card" />
                </div>
                <div>
                  <p className="text-lg font-semibold text-slate-700">JC Svejseteknik</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Contact Form */}
          <div className="lg:col-span-7 rounded-none border border-slate-200/60 bg-white shadow-lg p-8 md:p-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Udfyld kontaktformularen her</h2>
            <p className="text-sm text-slate-500 mb-8">
              Send os en besked med din forespørgsel, så vender vi tilbage til dig hurtigst muligt.
            </p>

            {status === 'success' ? (
              <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/20 p-8 text-center space-y-4">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/20 text-emerald-600 mx-auto">
                  <i className="fa-solid fa-circle-check text-3xl" />
                </div>
                <h3 className="text-xl font-bold text-slate-900">Tak for din besked!</h3>
                <p className="text-slate-600 leading-relaxed">
                  Vi har modtaget din henvendelse og vil vende tilbage til dig så hurtigt som muligt, typisk inden for 1-3 hverdage.
                </p>
                <button
                  onClick={() => setStatus('idle')}
                  className="rounded-md bg-brand-accent px-6 py-3 text-sm font-semibold tracking-wider text-white transition-all hover:bg-brand-accent/80"
                >
                  Send en ny besked
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Antispam Honeypot Field */}
                <div className="sr-only absolute">
                  <label htmlFor="website_url">Please leave this field empty</label>
                  <input type="text" id="website_url" name="website_url" tabIndex="-1" autoComplete="off" />
                </div>

                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="name" className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">
                      Navn <span className="text-brand-accent">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      placeholder="Dit fulde navn"
                      className="w-full rounded-md border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 placeholder-slate-400 focus:border-brand-accent focus:outline-none focus:ring-1 focus:ring-brand-accent"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">
                      Telefonnummer <span className="text-brand-accent">*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      placeholder="F.eks. 61 16 77 78"
                      className="w-full rounded-md border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 placeholder-slate-400 focus:border-brand-accent focus:outline-none focus:ring-1 focus:ring-brand-accent"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">
                    E-mail <span className="text-brand-accent">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    placeholder="din@email.dk"
                    className="w-full rounded-md border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 placeholder-slate-400 focus:border-brand-accent focus:outline-none focus:ring-1 focus:ring-brand-accent"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">
                    Beskrivelse <span className="text-brand-accent">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows="6"
                    placeholder="Beskriv din opgave, materialer, tykkelse, eller andre specifikationer..."
                    className="w-full rounded-md border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 placeholder-slate-400 focus:border-brand-accent focus:outline-none focus:ring-1 focus:ring-brand-accent"
                  ></textarea>
                </div>

                {status === 'error' && (
                  <div className="rounded-md bg-brand-orange/10 border border-brand-orange/20 p-4 text-sm text-brand-orange">
                    <i className="fa-solid fa-circle-exclamation mr-2" />
                    {errorMessage}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="w-full rounded-md bg-brand-accent py-4 text-sm font-semibold tracking-wider text-white transition-all hover:bg-brand-accent/80 disabled:opacity-50"
                >
                  {status === 'loading' ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="fa-solid fa-spinner animate-spin" />
                      Sender besked...
                    </span>
                  ) : (
                    'Send besked'
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
